import { useState, KeyboardEvent, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { userService } from '../services/userService'
import { User } from '../types/user'
import { MentionAutocomplete } from './MentionAutocomplete'
import './ChatInput.css'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export function ChatInput({ onSend, disabled }: ChatInputProps) {
  const [message, setMessage] = useState('')
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 })
  const [cursorPosition, setCursorPosition] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const mentionAutocompleteRef = useRef<HTMLDivElement>(null)

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAll(),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim() && !disabled) {
      onSend(message)
      setMessage('')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    const cursorPos = e.target.selectionStart
    setMessage(value)
    setCursorPosition(cursorPos)

    // Check for @ mention
    const textBeforeCursor = value.substring(0, cursorPos)
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/)

    if (mentionMatch) {
      const query = mentionMatch[1]
      setMentionQuery(query)
      setShowMentionAutocomplete(true)

      // Calculate position for autocomplete dropdown
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect()
        // Simple positioning - adjust as needed
        setMentionPosition({
          top: rect.top - 220, // Above the input
          left: rect.left + 10,
        })
      }
    } else {
      setShowMentionAutocomplete(false)
    }
  }

  const handleSelectMention = (user: User) => {
    const textBeforeCursor = message.substring(0, cursorPosition)
    const textAfterCursor = message.substring(cursorPosition)
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/)

    if (mentionMatch) {
      const beforeMention = textBeforeCursor.substring(0, mentionMatch.index)
      const mentionName = (user.firstName || user.username || user.id).trim().split(/\s+/)[0]
      const newMessage = `${beforeMention}@${mentionName} ${textAfterCursor}`
      setMessage(newMessage)
      setShowMentionAutocomplete(false)
      setMentionQuery('')

      // Set cursor position after mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = beforeMention.length + mentionName.length + 2
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
          textareaRef.current.focus()
        }
      }, 0)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentionAutocomplete) {
      // Let MentionAutocomplete handle arrow keys and enter
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      const isInsideInput = wrapperRef.current?.contains(target)
      const isInsideDropdown = mentionAutocompleteRef.current?.contains(target)
      if (!isInsideInput && !isInsideDropdown) {
        setShowMentionAutocomplete(false)
      }
    }

    if (showMentionAutocomplete) {
      document.addEventListener('click', handleClickOutside)
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [showMentionAutocomplete])

  return (
    <>
      <form className="chat-input-form" onSubmit={handleSubmit}>
        <div ref={wrapperRef} className="chat-input-wrapper">
          <textarea
            ref={textareaRef}
            className="chat-input"
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Enter to send, Shift+Enter for new line, @ to mention)"
            disabled={disabled}
            rows={1}
          />
          {showMentionAutocomplete && (
            <MentionAutocomplete
              query={mentionQuery}
              users={users}
              isLoading={usersLoading}
              onSelect={handleSelectMention}
              onClose={() => setShowMentionAutocomplete(false)}
              position={mentionPosition}
              containerRef={mentionAutocompleteRef}
            />
          )}
        </div>
        <button
          type="submit"
          className="chat-send-button"
          disabled={!message.trim() || disabled}
          aria-label="Send message"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17.5 2.5L8.75 11.25M17.5 2.5L12.5 17.5L8.75 11.25M17.5 2.5L2.5 7.5L8.75 11.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </>
  )
}































